import React, { useEffect, useRef, useState } from "react";
import { Redirect } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetMyActivation,
  useRequestAccountActivation,
  useVerifyAccountActivation,
  getGetMyActivationQueryKey,
  getGetMeQueryKey,
  type AccountActivationStatus,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { useToast } from "@/hooks/use-toast";
import {
  Check,
  Loader2,
  Mail,
  ShieldCheck,
  Smartphone,
} from "lucide-react";

// Surface a server-provided error message (`{ error }` / `{ message }`) when one
// is available, falling back to a generic line otherwise.
function apiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "data" in err) {
    const data = (err as { data?: unknown }).data;
    if (data && typeof data === "object") {
      const message =
        (data as { message?: unknown }).message ??
        (data as { error?: unknown }).error;
      if (typeof message === "string" && message.trim()) return message;
    }
  }
  return fallback;
}

/**
 * Full-screen account activation. EVERY new user (any role) must prove ownership
 * of both their mobile number (SMS OTP) and their email (email OTP) before the
 * account is active. Mirrors the bank-change dual-channel OTP security flow.
 * Once both channels verify, the backend marks the user activated and the
 * outer gate cascades them onward (agents -> onboarding, staff -> profile).
 */
export function AccountActivate() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: status, isLoading } = useGetMyActivation();

  const request = useRequestAccountActivation();
  const verify = useVerifyAccountActivation();

  const [phase, setPhase] = useState<"phone" | "verify">("phone");
  const [phone, setPhone] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [emailCode, setEmailCode] = useState("");

  // Seed the phase once from the server: resume an in-progress session straight
  // at the verify step, otherwise start at phone entry.
  const initRef = useRef(false);
  useEffect(() => {
    if (!initRef.current && status) {
      initRef.current = true;
      setPhase(status.hasSession ? "verify" : "phone");
    }
  }, [status]);

  const setStatus = (next: AccountActivationStatus) => {
    queryClient.setQueryData(getGetMyActivationQueryKey(), next);
  };

  const smsVerified = status?.smsVerified ?? false;
  const emailVerified = status?.emailVerified ?? false;
  const canResend = status?.canResend ?? true;

  const sendCodes = async () => {
    if (!phone.trim()) return;
    try {
      const next = await request.mutateAsync({ data: { phone: phone.trim() } });
      setStatus(next);
      setSmsCode("");
      setEmailCode("");
      setPhase("verify");
      toast({
        title: "Codes sent",
        description: "Check your phone and email for your verification codes.",
      });
    } catch (err) {
      toast({
        title: "Could not send codes",
        description: apiErrorMessage(err, "Please try again in a moment."),
        variant: "destructive",
      });
    }
  };

  const verifyChannel = async (channel: "sms" | "email", code: string) => {
    try {
      const next = await verify.mutateAsync({ data: { channel, code } });
      setStatus(next);
      if (channel === "sms") setSmsCode("");
      else setEmailCode("");
      if (next.activated) {
        // Account is live — refresh /me so the gate routes the user onward.
        queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
        toast({
          title: "Account activated",
          description: "Welcome to Mooya. Taking you in…",
        });
      } else {
        toast({
          title: channel === "sms" ? "Phone verified" : "Email verified",
          description:
            next.smsVerified && next.emailVerified
              ? "Both channels verified."
              : "One more channel to verify.",
        });
      }
    } catch (err) {
      toast({
        title: "Incorrect or expired code",
        description: apiErrorMessage(err, "Check the code and try again."),
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-gray-50 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  // Already activated — let the outer gate decide where to send the user next.
  if (status?.activated) {
    return <Redirect to="/home" />;
  }

  return (
    <div className="min-h-[100dvh] w-full bg-[#0B132B] text-white flex flex-col items-center px-4 py-10">
      <div className="w-full max-w-[460px] flex flex-col">
        <header className="flex items-center gap-2 mb-10">
          <img
            src={`${import.meta.env.BASE_URL}mooya-mark.png`}
            alt="Mooya"
            className="w-8 h-8 object-contain"
          />
          <span className="font-bold text-lg tracking-tight">Mooya</span>
        </header>

        <div className="w-12 h-12 rounded-full bg-[#E6007E]/15 flex items-center justify-center mb-6">
          <ShieldCheck className="w-6 h-6 text-[#E6007E]" />
        </div>

        {phase === "phone" ? (
          <>
            <h1 className="text-3xl font-bold tracking-tight mb-3">
              Activate your account
            </h1>
            <p className="text-slate-300 mb-8">
              For your security, we&apos;ll send a code to your mobile and your
              email. You&apos;ll need both to activate your account.
            </p>

            <div className="space-y-2 mb-6">
              <Label htmlFor="activate-phone" className="text-slate-200">
                Mobile number
              </Label>
              <Input
                id="activate-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                inputMode="tel"
                autoComplete="tel"
                placeholder="082 123 4567"
                className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                onKeyDown={(e) => {
                  if (e.key === "Enter") sendCodes();
                }}
              />
              <p className="text-xs text-slate-400">
                Enter your South African mobile number.
              </p>
            </div>

            <Button
              onClick={sendCodes}
              disabled={!phone.trim() || request.isPending}
              className="bg-[#E6007E] hover:bg-[#c4006b] text-white rounded-xl w-full h-11"
            >
              {request.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Send verification codes"
              )}
            </Button>
          </>
        ) : (
          <>
            <h1 className="text-3xl font-bold tracking-tight mb-3">
              Verify it&apos;s you
            </h1>
            <p className="text-slate-300 mb-8">
              Enter the codes we sent by SMS and email. Both must be verified to
              activate your account.
            </p>

            <div className="space-y-6 mb-6">
              <OtpChannel
                icon={<Smartphone className="w-4 h-4" />}
                title="SMS code"
                target={status?.maskedPhone ?? null}
                value={smsCode}
                onChange={setSmsCode}
                verified={smsVerified}
                onVerify={() => verifyChannel("sms", smsCode)}
                pending={verify.isPending}
              />
              <OtpChannel
                icon={<Mail className="w-4 h-4" />}
                title="Email code"
                target={status?.maskedEmail ?? null}
                value={emailCode}
                onChange={setEmailCode}
                verified={emailVerified}
                onVerify={() => verifyChannel("email", emailCode)}
                pending={verify.isPending}
              />
            </div>

            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={sendCodes}
                disabled={request.isPending || !canResend || !phone.trim()}
                className="text-sm font-medium text-[#E6007E] hover:underline disabled:opacity-50 disabled:no-underline text-left"
              >
                {canResend
                  ? "Didn't get a code? Resend both"
                  : "Resend limit reached — contact support"}
              </button>
              <button
                type="button"
                onClick={() => setPhase("phone")}
                disabled={request.isPending}
                className="text-sm font-medium text-slate-300 hover:text-white text-left disabled:opacity-50"
              >
                Use a different number
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function OtpChannel({
  icon,
  title,
  target,
  value,
  onChange,
  verified,
  onVerify,
  pending,
}: {
  icon: React.ReactNode;
  title: string;
  target: string | null;
  value: string;
  onChange: (value: string) => void;
  verified: boolean;
  onVerify: () => void;
  pending: boolean;
}) {
  return (
    <div className="space-y-2 rounded-2xl bg-white/5 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-white">
          <span className="text-slate-400">{icon}</span>
          {title}
          {target ? (
            <span className="text-xs font-normal text-slate-400">
              to {target}
            </span>
          ) : null}
        </div>
        {verified ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#00A859]">
            <Check className="w-3.5 h-3.5" /> Verified
          </span>
        ) : null}
      </div>
      {verified ? null : (
        <div className="flex items-center gap-2">
          <InputOTP
            maxLength={6}
            value={value}
            onChange={onChange}
            aria-label={`${title} verification code`}
            autoComplete="one-time-code"
            inputMode="numeric"
          >
            <InputOTPGroup>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <InputOTPSlot
                  key={i}
                  index={i}
                  className="border-white/20 text-white"
                />
              ))}
            </InputOTPGroup>
          </InputOTP>
          <Button
            type="button"
            variant="outline"
            onClick={onVerify}
            disabled={value.length !== 6 || pending}
            className="shrink-0 border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"
          >
            {pending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify"}
          </Button>
        </div>
      )}
    </div>
  );
}
